import React from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme.js';
import { ImpactBadge, DataTable, InsightList, WhyThisMatters } from '../components/Shared.jsx';
import { formatCadenceChip } from '../utils/formatUtils.js';

export function CadenceFormat({ data }) {
  const { publishingCadence = {}, duration = {}, releaseTime = {}, suggestions = [], insights = [] } = data;
  const toTwoDecimals = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '—';
  };

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 1.5, mb: 3 }}>
        {[
          ['Pattern',           formatCadenceChip(data)],
          ['Avg Days Between',  toTwoDecimals(publishingCadence.averageDaysBetweenEpisodes)],
          ['Consistency',       publishingCadence.consistency !== undefined && publishingCadence.consistency !== null ? `${toTwoDecimals(publishingCadence.consistency)}%` : '—'],
          ['Avg Duration',      duration.averageMinutes !== undefined && duration.averageMinutes !== null ? `${toTwoDecimals(duration.averageMinutes)}m` : '—'],
          ['Duration Trend',    duration.trend],
          ['Release Day',       releaseTime.dayOfWeek || '—'],
        ].map(([label, value]) => (
          <Box key={label} sx={{ p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1.5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.primary, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{value ?? '—'}</Typography>
            <Typography sx={{ fontSize: 11, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {duration.distribution && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Episode Length Distribution</Typography>
          <DataTable
            columns={['Short (<20m)', 'Medium (20-45m)', 'Long (>45m)']}
            rows={[[`${duration.distribution.short}%`, `${duration.distribution.medium}%`, `${duration.distribution.long}%`]]}
          />
        </Box>
      )}

      {suggestions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Suggestions</Typography>
          {suggestions.map((s, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: 'rgba(45,212,191,0.07)', border: `1px solid rgba(45,212,191,0.2)`, borderRadius: 1, display: 'flex', gap: 1 }}>
              <ImpactBadge value={s.impact} />
              <Box sx={{ minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{s.suggestion}</Typography>
                <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.25, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{s.reasoning}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <InsightList insights={insights} />
      <WhyThisMatters text="Consistent publishing builds listener expectations and algorithmic favor." citations={data.expertCitations} />
    </Box>
  );
}
